// src/features/ceiling/CeilingBar.tsx
import React, { useMemo } from "react";
import { Group, Text, Divider, ActionIcon, Badge, Tooltip, Button, Stack } from "@mantine/core";
import { FaHistory } from "react-icons/fa";
import { ModuleToolBarShell } from "../ModuleToolBarShell";
import { ActivateSnapSwitch } from "./ActivateSnapSwitch";
import { ActivateTrueSizeSwitch } from "./ActivateTrueSizeSwitch";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { SelectedRoomsActionsMenu } from "../../devices/SelectedRooms/SelectedRoomsActionsMenu";
import { SelectedRoomsValidationSummaryInline } from "../../devices/SelectedRooms/SelectedRoomsValidationSummaryInline";
import { selectDeviceHistory } from "../../history/deviceHistorySlice";
import { DesignLeftSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { setActiveLeftSideBarTabInRedux } from "../../designSlice";
import { thunkAutoPipeRoutingWithMockData } from "../../routing/autoPipeRouting/autoPipeRoutingThunks";
import { clearUnsavedRoutingItems } from "../../routing/unsavedRoutingSlice";
import { AutoPipeRoutingRunProgress } from "../../routing/autoPipeRouting/AutoPipeRoutingRunProgress";

export const CeilingBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const isDev = import.meta.env.DEV;

  const selectedRooms = useAppSelector((s) => s.design.selectedRooms);
  const hasSelectedRooms = (selectedRooms?.length ?? 0) > 0;

  const runSpaceValidationsInSelectedRooms = useAppSelector(
    (s) => s.design.runSpaceValidationsInSelectedRooms
  );

  const history = useAppSelector(selectDeviceHistory);

  const historyCount = useMemo(() => {
    return (history.undoStack?.length ?? 0) + (history.redoStack?.length ?? 0);
  }, [history.undoStack?.length, history.redoStack?.length]);

  const canShowHistory = historyCount > 0;

  const activeLeftSideBarTab = useAppSelector((s) => s.design.activeLeftSideBarTab);

  const toggleHistoryPanel = () => {
    const isOpen = activeLeftSideBarTab === DesignLeftSideBarTabs.DEVICE_HISTORY;
    dispatch(
      setActiveLeftSideBarTabInRedux(isOpen ? undefined : DesignLeftSideBarTabs.DEVICE_HISTORY)
    );
  };

  return (
    <ModuleToolBarShell>
      <Stack gap="sm" w="100%">
        <Group align="center" gap="md">
          {/* Tittel / kontekst */}
          <Group gap="sm" align="center">
            <Text fw={600} size="sm">
              Himlingsdesign
            </Text>
            <Divider orientation="vertical" h={24} />
          </Group>

          {/* Verktøy: snap to grid */}
          <ActivateSnapSwitch />
          <ActivateTrueSizeSwitch />

          {/* ✅ Historikk-indikator (vises kun når det finnes endringer) */}
          {canShowHistory && (
            <>
              <Divider orientation="vertical" h={24} />
              <Tooltip label={`Historikk (${historyCount})`} withArrow>
                <Group gap={8}>
                  <ActionIcon
                    variant="outline"
                    size="sm"
                    onClick={toggleHistoryPanel}
                    aria-label="Åpne historikk"
                  >
                    <FaHistory size={16} />
                  </ActionIcon>

                  <Badge
                    variant="outline"
                    size="sm"
                    style={{ cursor: "pointer" }}
                    onClick={toggleHistoryPanel}
                  >
                    {historyCount}
                  </Badge>
                </Group>
              </Tooltip>
            </>
          )}

          {/* Inline status for valgte rom */}
          {hasSelectedRooms && runSpaceValidationsInSelectedRooms && (
            <>
              <Divider orientation="vertical" h={24} />
              <SelectedRoomsValidationSummaryInline />
            </>
          )}

          {/* Actions på valgte rom (kun synlig når rom er valgt) */}
          {hasSelectedRooms && (
            <>
              <Divider orientation="vertical" h={24} />
              <SelectedRoomsActionsMenu />
            </>
          )}

          {isDev && (
            <>
              <Divider orientation="vertical" h={24} />
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    void dispatch(thunkAutoPipeRoutingWithMockData());
                  }}
                >
                  Mock Auto Pipe Routing
                </Button>
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    dispatch(clearUnsavedRoutingItems());
                  }}
                >
                  Clear Routing
                </Button>
              </Group>
            </>
          )}
        </Group>

        <AutoPipeRoutingRunProgress />
      </Stack>
    </ModuleToolBarShell>
  );
};
